/**
 * Shared conversion surfaces (M4). One component per job, used everywhere it
 * is needed, never copied:
 *
 * - {@link ProPathCard}: the Pro pitch (spotlight surface, double CTA, price or
 *   early-access note). Lesson end of the free path, dashboard, free course
 *   pages. Hidden for viewers who already have Pro.
 * - {@link LessonNextStep}: the end-of-lesson block. Keeps the momentum on the
 *   free path and turns the end of Course 2 into the strongest upsell moment.
 *
 * Value first: every surface leads with what the learner gets next, the price
 * is a footnote, and in early access the CTA is the founding list (no payment).
 * Every view and click is measured via `trackEvent`.
 */
import { ArrowRight, Check, Sparkles, Trophy } from 'lucide-react'
import { useEffect, useRef } from 'react'
import type { Course, Lesson } from '../content/catalog'
import { useCatalog } from '../content/catalog-context'
import { trackEvent } from '../lib/analytics'
import type { ConversionSurface } from '../lib/analytics'
import { localizedPath, useTranslation } from '../lib/i18n'
import { resolveLessonNextStep } from '../lib/lesson-next-step'
import { formatPrice, pricePerMonth } from '../lib/pricing'
import { FOUNDING_HASH } from '../lib/pricing-cta'
import { useBillingStatus, useViewerEntitlement } from '../lib/use-billing'
import { ProBadge } from './pro-badge'

/** The first Pro course: where "see what Pro is" sends a curious learner. */
const FIRST_PRO_COURSE = 'modern-app-stack'

type Tracking = { course?: string; lesson?: string }

/** Fire `upsell_viewed` once, when at least half of the element is visible. */
function useViewedOnce(surface: ConversionSurface, tracking: Tracking) {
  const ref = useRef<HTMLElement | null>(null)
  const course = tracking.course
  const lesson = tracking.lesson
  useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          trackEvent('upsell_viewed', { surface, course, lesson })
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [surface, course, lesson])
  return ref
}

export function ProPathCard({
  surface,
  eyebrow,
  title,
  text,
  layout = 'wide',
  tracking = {},
}: {
  surface: ConversionSurface
  eyebrow: string
  title: string
  text: string
  /** `wide`: copy and benefits side by side. `compact`: stacked, for sidebars. */
  layout?: 'wide' | 'compact'
  tracking?: Tracking
}) {
  const { locale, t, dict } = useTranslation()
  const live = useBillingStatus()
  const viewer = useViewerEntitlement()
  const ref = useViewedOnce(surface, tracking)
  // Pro members never see a pitch for what they already have. Until the
  // entitlement is known the card renders (SSR-identical, no layout shift
  // for the common case: anonymous and free learners).
  if (viewer.ready && viewer.isPro) return null

  const early = live === false
  const pricingHref = localizedPath('pricing', locale)
  const price = formatPrice(pricePerMonth('pro', 'year'), locale)
  const click = (cta: 'pricing' | 'founding' | 'pro_course') =>
    trackEvent('upsell_cta_clicked', { surface, cta, ...tracking })

  return (
    <aside
      ref={ref}
      className={`pro-path-card spotlight-surface is-${layout}`}
      aria-label={eyebrow}
      data-surface={surface}
    >
      <div className="pro-path-copy">
        <span className="pro-path-eyebrow">
          <Sparkles size={12} aria-hidden="true" />
          {eyebrow}
        </span>
        <h2>{title}</h2>
        <p>{text}</p>
      </div>
      <div className="pro-path-side">
        <ul className="pro-path-bullets">
          {dict.upsell.bullets.map((bullet) => (
            <li key={bullet}>
              <span aria-hidden="true">
                <Check size={13} strokeWidth={3} />
              </span>
              {bullet}
            </li>
          ))}
        </ul>
        <div className="pro-path-actions">
          <a
            className="pro-gate-primary"
            href={early ? `${pricingHref}${FOUNDING_HASH}` : pricingHref}
            onClick={() => click(early ? 'founding' : 'pricing')}
          >
            {early ? t('upsell.ctaFounding') : t('upsell.ctaPlans')}
            <ArrowRight size={16} aria-hidden="true" />
          </a>
          <a
            className="pro-gate-secondary"
            href={localizedPath('course', locale, { courseSlug: FIRST_PRO_COURSE })}
            onClick={() => click('pro_course')}
          >
            {t('upsell.ctaPreview')}
          </a>
        </div>
        <small className="pro-path-note">
          {early ? t('upsell.earlyNote') : t('pro.priceNote', { price })}
        </small>
      </div>
    </aside>
  )
}

function NextLessonCard({
  eyebrow,
  lesson,
  course,
  cta,
  kind,
  tracking,
}: {
  eyebrow: string
  lesson: Lesson
  course: Course | null
  cta: string
  kind: 'next_lesson' | 'next_course'
  tracking: Tracking
}) {
  const { locale } = useTranslation()
  return (
    <aside className="next-step-card" aria-label={eyebrow} data-next-step={kind}>
      <span className="eyebrow">{eyebrow}</span>
      <div className="next-step-body">
        <span className="next-step-number" aria-hidden="true">
          {lesson.number}
        </span>
        <div>
          <strong>
            {course ? `${course.title}: ` : ''}
            {lesson.title}
          </strong>
          <p>{lesson.summary}</p>
          <small>
            {lesson.duration}
            {lesson.access === 'pro' ? <ProBadge className="is-inline" /> : null}
          </small>
        </div>
      </div>
      <a
        className="primary-button"
        href={localizedPath('lesson', locale, {
          courseSlug: lesson.courseSlug,
          lessonSlug: lesson.slug,
        })}
        onClick={() =>
          trackEvent('upsell_cta_clicked', {
            surface: 'lesson_next_step',
            cta: kind,
            ...tracking,
          })
        }
      >
        {cta}
        <ArrowRight size={16} aria-hidden="true" />
      </a>
    </aside>
  )
}

/** End-of-lesson "what now" block, driven by {@link resolveLessonNextStep}. */
export function LessonNextStep(props: { courseSlug: string; lessonSlug: string }) {
  // `#next-step` is the target of the mobile lesson bar's "Next" button.
  return (
    <div id="next-step" className="lesson-next-step">
      <LessonNextStepBody {...props} />
    </div>
  )
}

function LessonNextStepBody({
  courseSlug,
  lessonSlug,
}: {
  courseSlug: string
  lessonSlug: string
}) {
  const { locale, t } = useTranslation()
  const catalog = useCatalog()
  const step = resolveLessonNextStep({
    courses: catalog.courses,
    lessons: catalog.lessons,
    courseSlug,
    lessonSlug,
  })
  const tracking = { course: courseSlug, lesson: lessonSlug }
  if (!step) return null

  if (step.kind === 'next_lesson') {
    return (
      <NextLessonCard
        kind="next_lesson"
        eyebrow={t('upsell.nextEyebrow')}
        lesson={step.lesson}
        course={null}
        cta={t('upsell.nextLessonCta', { number: step.lesson.number })}
        tracking={tracking}
      />
    )
  }
  if (step.kind === 'next_course') {
    return step.lesson ? (
      <NextLessonCard
        kind="next_course"
        eyebrow={t('upsell.nextCourseEyebrow')}
        lesson={step.lesson}
        course={step.course}
        cta={t('upsell.nextCourseCta', { number: step.course.order })}
        tracking={tracking}
      />
    ) : null
  }
  if (step.kind === 'pro_upsell') {
    return (
      <ProPathCard
        surface="lesson_next_step"
        eyebrow={t('upsell.lessonEndEyebrow')}
        title={t('upsell.lessonEndTitle')}
        text={t('upsell.lessonEndText')}
        tracking={tracking}
      />
    )
  }
  return (
    <aside className="next-step-card is-finished" data-next-step="finished">
      <span className="eyebrow">
        <Trophy size={12} aria-hidden="true" /> {t('upsell.finishedEyebrow')}
      </span>
      <div className="next-step-body">
        <div>
          <strong>{t('upsell.finishedTitle')}</strong>
          <p>{t('upsell.finishedText')}</p>
        </div>
      </div>
      <a
        className="primary-button"
        href={localizedPath('progress', locale)}
        onClick={() =>
          trackEvent('upsell_cta_clicked', {
            surface: 'lesson_next_step',
            cta: 'dashboard',
            ...tracking,
          })
        }
      >
        {t('upsell.finishedCta')}
        <ArrowRight size={16} aria-hidden="true" />
      </a>
    </aside>
  )
}
